import { DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input } from '@angular/core';
import { formatSeconds, isEmptyArray, isEmptyObject, isStringEmpty, stringEmptyDefault } from '@common/helpers';
import { FLIGHT_STATUS_CODE } from '@common/helpers/constants/my-booking/my_booking_service.contant';
import { MListData } from '@common/interfaces';
import { AircraftCabinAll } from '@common/interfaces/master/aircraft_cabin_all';
import { AirlineI18nAll } from '@common/interfaces/master/airline_i18n_all';
import { EquipmentI18nJoinPk } from '@common/interfaces/master/equipment_pk';
import { MasterDataS01P031 } from '@common/interfaces/mybooking-itinerary/mybooking-itinerary.interface';
import { ServicingCommonStoreService } from '@common/services';
import { MyBookingItineraryService } from '@common/services/my-booking/my-booking-itinerary.service';
import { TripSummaryService } from '@common/services/my-booking/trip-summary.service';
import { SupportComponent } from '@lib/components/support-class';
import { BookingType, DeviceType } from '@lib/interfaces';
import { StaticMsgPipe } from '@lib/pipes';
import { CommonLibService } from '@lib/services';
import { Subscription } from 'rxjs';
import {
  GetOrderResponseData,
  GetOrderResponseDataAir,
  GetOrderResponseDataSeats,
  OrderSeatItemSeatSelection,
  Type1,
} from 'src/sdk-servicing';
import { AppConstants } from '@conf/app.constants';

@Component({
  selector: 'asw-waiting',
  templateUrl: './waiting.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WaitingComponent extends SupportComponent {
  @Input() boundIdx!: number;
  @Input() flightIdx!: number;

  id = this.constructor.name;
  flight: Type1 = {};
  airlineLink: string = '';
  deviceType: DeviceType = '';
  isSeatmapEligible = false;
  isStops: boolean = false;
  isShowACV: boolean = false;
  aircraftCabinMaster: {
    acvCharacteristicMessageKeys?: string[];
    productUrls?: string[];
  } = {};

  private _subscriptions = new Subscription();
  private _mListDataPD930: Array<MListData> = [];
  private _mAirline: Array<AirlineI18nAll> = [];
  private _mAirlineI18N: Array<AirlineI18nAll> = [];
  private _equipmentI18nJoinPk: Array<EquipmentI18nJoinPk> = [];
  private _seats: GetOrderResponseDataSeats = {};
  private _travelerIds: Array<string> = [];
  private _isAwardBooking?: boolean;

  pnr: GetOrderResponseData = {};

  get flightDuration(): string {
    return formatSeconds(this.flight.duration);
  }

  pointOfSaleId: string = '';

  /** 定数用 */
  public appConstants = AppConstants;

  constructor(
    public tripSummarySvc: TripSummaryService,
    private _myBookingItineraryService: MyBookingItineraryService,
    private _servicingCommonStoreService: ServicingCommonStoreService,
    private _staticMsg: StaticMsgPipe,
    private _datePipe: DatePipe,
    private _change: ChangeDetectorRef,
    private _common: CommonLibService
  ) {
    super(_common);
    this.deviceType = _common.aswContextStoreService.aswContextData.deviceType ?? '';
    this.pointOfSaleId = _common.aswContextStoreService.aswContextData.pointOfSaleId ?? '';
  }

  override init(): void {
    this._subscriptions.add(
      this._servicingCommonStoreService.getServicingCommonModel$().subscribe((servicingCommon) => {
        const pnr = (servicingCommon.getOrder as GetOrderResponseData) ?? {};
        this._seats = pnr.seats ?? {};
        this._travelerIds = (pnr.travelers?.map((item) => item.id) as Array<string>) ?? [];
        this.flight = pnr.air?.bounds?.at(this.boundIdx)?.flights?.at(this.flightIdx) ?? {};
        // 提携: セグメント毎の案内要否(isEligible)で判断するように修正 ※UIUXs2不備
        this.isSeatmapEligible = pnr.orderEligibilities?.seatmap?.[this.flight?.id ?? '']?.isEligible ?? false;
        this._isAwardBooking = pnr.orderType?.isAwardBooking;
        this._change.markForCheck();
      })
    );
    this._subscriptions.add(
      this._myBookingItineraryService.masterData$.subscribe((result: MasterDataS01P031) => {
        this._mAirline = result.carrierList ?? [];
        this._mAirlineI18N = result.internationalCarrierCodes ?? [];
        this._equipmentI18nJoinPk = result.equipmentI18nJoinPk ?? [];
        this._mListDataPD930 = result.mListDataPD930 ?? [];
        this.aircraftCabinMaster = this.getAircraftCabinMaster(result.aircraftCabinAll ?? []);
        this.isShowACV = this.aircraftCabinMaster.acvCharacteristicMessageKeys?.some((item) => item !== '') ?? false;
        this._change.markForCheck();
      })
    );
  }

  /** (1.1.28) (1) 運航キャリア識別 */
  get showIconANA(): boolean {
    /** 1)当該セグメント情報.isNhGroupOperated=true (NHグループ運航便)の場合、ANAのアイコンを表示する。*/
    return Boolean(this.flight?.isNhGroupOperated === true);
  }

  /** (1.1.28) (2) 運航キャリア識別 */
  get showIconStarAlliance(): boolean {
    /** 2)当該セグメント情報.isNhGroupOperated=false、かつ当該セグメント.isStarAllianceOperated=trueの場合、スターアライアンスのアイコンを表示する。*/
    return Boolean(this.flight?.isNhGroupOperated === false && this.flight?.isStarAllianceOperated === true);
  }

  /** (1.1.28) (3) 運航キャリア識別 */
  get hideIconOperatingCarrier(): boolean {
    /** 3)当該セグメント情報.isNhGroupOperated=false、かつ当該セグメント.isStarAllianceOperated=falseの場合、非表示とする。*/
    return Boolean(this.flight?.isNhGroupOperated === false && this.flight?.isStarAllianceOperated === false);
  }

  /** (1.1.29) (1) 運航キャリア名称 */
  get carrierUrl(): string {
    /**
     * 1)当該セグメント情報.operatingAirlineCode≠"NH"(運航キャリアコードがNHでない)の場合、
     * 当該セグメント情報.operatingAirlineCode、 を基に「イベントID(1)」で取得したキャリアリストをフィルタして、
     * 運航キャリアURLを取得する。
     */
    let operatingAirlineUrl: string = '';
    if (this.flight.operatingAirlineCode !== AppConstants.CARRIER.TWO_LETTER) {
      operatingAirlineUrl =
        this._mAirline.find((item) => item.airline_code === this.flight.operatingAirlineCode)?.operating_airline_url ??
        '';
    }

    return operatingAirlineUrl;
  }

  /** (1.1.29) (2) 運航キャリア名称 */
  get carrierName(): string {
    /** 2)当該セグメント情報.operatingAirlineCodeを基に「イベントID(1)」で取得したキャリア(国際化)リストをフィルタして、キャリア名称を取得する。*/
    return (
      this._mAirlineI18N.find((airline) => airline.airline_code === this.flight.operatingAirlineCode)?.airline_name ??
      ''
    );
  }

  /** (1.1.29) (3, 3.1) 運航キャリア名称 */
  get showOperatingCarrierName(): boolean {
    /**
     * 3)以下の条件に応じて表示する。運航キャリアURLが存在する場合、運航キャリアURLで運航キャリア名称を表示する。
     * 3.1)キャリア名称が存在する場合、キャリア名称を表示する。
     */
    return !isStringEmpty(this.carrierUrl) && !isStringEmpty(this.carrierName) ? true : false;
  }

  /** (1.1.29) (3.2) 運航キャリア名称 */
  get operatingAirlineName(): string {
    /** 3.2)キャリア名称が存在しない場合、当該セグメント情報.operatingAirlineNameを表示する。*/
    return this.flight?.operatingAirlineName ?? '';
  }

  // (1.1.30) 機種コード
  getAircraft(): { url: string; name: string } {
    /** 1)
     * 当該セグメント情報.isNhGroupOperated=true(NHグループ運航便)の場合、
     * 当該セグメント情報.aircraftConfigurationVersionを基に「イベントID(1)」で取得した機材リストをフィルタして、
     * 機種URLを取得する。
     */
    const url = !this.flight.isNhGroupOperated
      ? ''
      : this._equipmentI18nJoinPk.find((item) => item.acv === this.flight.aircraftConfigurationVersion)
          ?.aircraft_type_url ?? '';
    /**
     * ※ユーザ共通情報.data.aswContext.deviceType=“SP”版の場合のみ、
     * 当該セグメント情報.aircraftName(機種名称)
     * を読み上げ用ラベルとして設置する。
     */
    const displayName =
      this.deviceType === DeviceType.SMART_PHONE
        ? this._staticMsg.transform('reader.displayAircraft', { '0': stringEmptyDefault(this.flight?.aircraftName) })
        : stringEmptyDefault(this.flight?.aircraftCode);
    return {
      url: url,
      name: displayName,
    };
  }

  // (1.1.32) 座席指定状況
  getSeatNumberList(): string {
    // 1) 座席番号リストを空のリストとして作成する。
    let seatNumberList: Array<string> = [];
    const seatOfFlight = this._seats[(this.flight.id ?? '') as keyof typeof this._seats] ?? {};
    const keys = Object.keys(seatOfFlight).filter((item) => this._travelerIds.includes(item));
    // <以下、PNR情報取得API応答.data.seats.<当該セグメント情報.id>の要素数繰り返し>(それぞれの要素が当該セグメント情報)
    keys.forEach((travelerId) => {
      let traveler = seatOfFlight[travelerId as keyof typeof seatOfFlight] ?? {};
      /** 1.1)
       * 当該セグメント情報.<搭乗者ID>.seatSelection.couchSeatNumber ≠ ""、か
       * 当該セグメント情報.<搭乗者ID>.seatSelection.isCouchRepresentative = true(カウチ代表者)の場合、
       * 座席番号リストに当該セグメント情報.<搭乗者ID>.seatSelection.couchSeatNumberを追加する。
       */
      /** 1.2) 上記以外、
       * 1.2.1) 当該セグメント情報.<搭乗者ID>.seatSelection.serviceStatus="confirmed"の場合、
       *        座席番号リストに当該セグメント情報.<搭乗者ID>.seatSelection.seatNumbers[] を追加する。
       * 1.2.2) 当該セグメント情報.<搭乗者ID>.seatSelection.serviceStatus≠"confirmed"の場合、
       *        座席番号リストに未登録の旨を追加する。
       */
      traveler?.seatSelection?.serviceStatus === OrderSeatItemSeatSelection.ServiceStatusEnum.Confirmed
        ? seatNumberList.push(...(traveler?.seatSelection?.seatNumbers ?? []))
        : seatNumberList.push(this._staticMsg.transform('label.notRegisteredSeatNumber'));
    });
    // 2) 座席番号リストの件数>0の場合、","区切る文字列型を作成する。
    return seatNumberList.length > 0
      ? seatNumberList.filter(Boolean).join(this._staticMsg.transform('label.flight.separator'))
      : '';
  }

  /**
   * 提携：IBX:イレギュラー発生便（当該セグメント情報.scheduleChangeTypeが設定されていた場合）はセグメントステータスを非表示とする。
   *
   * @returns チェック結果（true:イレギュラー便, false:それ以外)
   */
  isScheduleChanged(): boolean {
    switch (this.flight.scheduleChangeType) {
      // 当該セグメント情報.scheduleChangeType="timeChanged"(マイナースケジュールチェンジ)の場合、
      case Type1.ScheduleChangeTypeEnum.TimeChanged:
        return true;
      // 当該セグメント情報.scheduleChangeType="flightChanged"(メジャースケジュールチェンジ)の場合、
      case Type1.ScheduleChangeTypeEnum.FlightChanged:
        return true;
      // 当該セグメント情報.scheduleChangeType="waitlistedTimeChanged"(マイナースケジュールチェンジ(空席待ち))の場合、
      case Type1.ScheduleChangeTypeEnum.WaitlistedTimeChanged:
        return true;
      // 当該セグメント情報.scheduleChangeType="waitlistedFlightChanged"(メジャースケジュールチェンジ(空席待ち))の場合、
      case Type1.ScheduleChangeTypeEnum.WaitlistedFlightChanged:
        return true;
      default:
        return false;
    }
  }

  // (1.1.33) ステータス - ステータスについて、以下の表示を行う。
  getStatus(): { style: boolean; label: string } {
    /**
     * ただし、当該セグメント情報.upgradeType="positive"(ポジティブアップグレード)、かつ
     * 当該セグメント情報.upgradeWaitlisted(アップグレード空席待ち情報)が存在する場合はアップグレード区分が存在しないものとして扱う。
     */

    // 1) 当該セグメント情報.isOpenSegment=trueの場合、OPEN便の旨を表示する。
    if (this.flight.isOpenSegment) return { style: false, label: 'label.openFlight' };
    // 2) 当該セグメント情報.isFlown=trueの場合、出発済みの旨を表示する。
    if (this.flight.isFlown) return { style: false, label: 'label.flown' };
    // 3) 当該セグメント情報.scheduleChangeTypeが存在する場合、区分に応じて以下いずれかの旨を赤色かつ太字で表示する。
    if (this.flight.scheduleChangeType) return { style: true, label: this.getScheduleChangeType() };
    // 4) 当該セグメント情報.statusCode="UN"の場合、セグメントが取り消されている旨を赤色かつ太字で表示する。
    if (this.flight.statusCode === FLIGHT_STATUS_CODE.UN) return { style: true, label: 'label.cancelledFlight' };
    // 5) 当該セグメント情報.statusType="neededCallCenterHandling"(要確認ステータス)の場合、確認を促す旨を赤色かつ太字で表示する。
    if (this.flight.statusType === Type1.StatusTypeEnum.NeededCallCenterHandling)
      return { style: true, label: 'label.caution' };
    // 7) 当該セグメント情報.statusType="confirmed"「確定済み」の場合、座席確定済みの旨を表示する。
    if (this.flight.statusType === Type1.StatusTypeEnum.Confirmed)
      return { style: false, label: 'label.seatsAvailable' };
    // 8) 当該セグメント情報.statusType="waitlisted"「空席待ち」の場合、空席待ちの旨を表示する。
    if (this.flight.statusType === Type1.StatusTypeEnum.Waitlisted) return { style: false, label: 'label.seatWaiting' };
    // 9) 上記以外の場合、その他のステータスの旨を表示する。
    return { style: false, label: 'label.hyphen' };
  }

  // (1.1.33) ステータス (3)
  getScheduleChangeType(): string {
    switch (this.flight.scheduleChangeType) {
      // 3.1) 当該セグメント情報.scheduleChangeType="timeChanged"(マイナースケジュールチェンジ)の場合、
      case Type1.ScheduleChangeTypeEnum.TimeChanged:
        // チェンジスケジュールチェンジかつ座席確定済みの旨を表示する。
        return 'label.fixedSeat.flightChanged';
      // 3.2) 当該セグメント情報.scheduleChangeType="flightChanged"(メジャースケジュールチェンジ)の場合、
      case Type1.ScheduleChangeTypeEnum.FlightChanged:
        // スケジュールチェンジ後のセグメントである旨を表示する。
        return 'label.fixedSeat.flightAfterChange';
      // 3.3) 当該セグメント情報.scheduleChangeType="waitlistedTimeChanged"(マイナースケジュールチェンジ(空席待ち))の場合、
      case Type1.ScheduleChangeTypeEnum.WaitlistedTimeChanged:
        // スケジュールチェンジかつ空席待ちである旨を表示する。
        return 'label.seatWaiting.flightChanged';
      // 3.4) 当該セグメント情報.scheduleChangeType="waitlistedFlightChanged"(メジャースケジュールチェンジ(空席待ち))の場合、
      case Type1.ScheduleChangeTypeEnum.WaitlistedFlightChanged:
        // スケジュールチェンジ後のセグメントでありかつ空席待ちの旨を表示する。
        return 'label.seatWaiting.flightAfterChange';
      default:
        return '';
    }
  }

  // (1.1.42) ACVに応じたラベルもしくは画像
  getAircraftCabinMaster(master: Array<AircraftCabinAll>): {
    acvCharacteristicMessageKeys: string[];
    productUrls: string[];
  } {
    /** 1)
     * 当該セグメント情報.aircraftConfigurationVersionを基に「イベントID(1)」で取得したキャビンクラス別機材情報リストをフィルタして、
     * ACV特性案内文言キーリストを取得する。
     */
    const result = master.filter(
      (item, index, self) =>
        index === self.findIndex((aircraft) => aircraft.acv === item.acv && aircraft.cabin === item.cabin) &&
        item.acv === this.flight.aircraftConfigurationVersion &&
        item.cabin === this.flight.cabin
    );
    return {
      acvCharacteristicMessageKeys: result.map((item) => item.acv_characteristic_message_key),
      productUrls: result.map((item) => item.product_url),
    };
  }

  // (1.1.44) 途中寄港数 & (1.1.45) 寄港地点名称
  getStopFlight(): { times: number; locations: string } {
    let stopFlightList: string[] = [];
    // <以下、当該セグメント情報.stops[]の要素数繰り返し>(それぞれの要素が当該途中寄港地点情報)
    this.flight.stops?.forEach((stop) => {
      // 1.1) 当該途中寄港地点情報.locationCodeを基に「イベントID(1)」で取得した空港リストをフィルタして、空港名称を取得する。
      // 1.2) 取得できない場合は当該途中寄港地点情報.locationNameを表示する。
      stopFlightList.push(this._myBookingItineraryService.getAirportLocationName(stop.locationCode, stop.locationName));
    });
    return {
      times: this.flight.stops?.length ?? 0,
      // 1.3) ...で区切る空港名称を連携する[TODO]
      locations: stopFlightList.join(', '),
    };
  }

  /** (3) 「座席指定状況」ボタン押下時 */
  clickSeatReservationStatus() {
    return this._myBookingItineraryService.functionList['clickSeatReservationStatus'](this.flight.id ?? '');
  }

  // 提携: 運航キャリア名称を表示するかどうかの判定(自社オペの場合、false)
  get isDisplayCarrierName(): boolean {
    return this.flight.operatingAirlineCode !== this.appConstants.CARRIER.TWO_LETTER;
  }

  override reload(): void {
    // Add comment to passed SonarLint
  }

  override destroy(): void {
    if (this._subscriptions) {
      this._subscriptions.unsubscribe();
    }
  }
}
